import type { LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GuestSheet } from '../ui';
import type { ArrivalStep } from '../useGuestSession';
import { ArrivalStepCard } from './ArrivalStepCard';
import { ArrivalStepGallery } from './ArrivalStepGallery';

interface Props {
  steps: ArrivalStep[];
  /** Guest-Bearer-Token für die auth-geschützten Bild-URLs. */
  token: string | undefined;
  /** Kopf links; rechts steht bei mehreren Karten der Wisch-Hinweis. */
  header: ReactNode;
  labelledBy: string;
  /** Anfahrt: nummerierte Schritte. Ausweichmöglichkeiten: gleichrangig, ohne Nummer. */
  numbered: boolean;
  /** Ersatz für eine Karte ohne Foto. */
  icon: LucideIcon;
  /** Zusatz im Fenster, etwa die Freigabezeit eines gesperrten Schritts. */
  renderExtra?: (step: ArrivalStep) => ReactNode;
  id?: string;
}

/**
 * Karten zum Wischen (Canvas „Reisebegleiter“): Foto, Titel und der Anfang des Texts; ein Tippen
 * öffnet den ganzen Eintrag mit allen Bildern im Fenster. Schmal bis an den Rand gezogen, damit
 * die nächste Karte anschaut; ab breitem Bereich zwei Spalten — gemessen am Bereich, nicht am
 * Fenster, sonst zeigte die Admin-Vorschau im Telefonrahmen das Desktop-Raster.
 */
export function StepCardRow({ steps, token, header, numbered, ...props }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(null);
  if (steps.length === 0) return null;
  const active = open === null ? undefined : steps[open];

  return (
    <section
      id={props.id}
      aria-labelledby={props.labelledBy}
      tabIndex={-1}
      className="@container scroll-mt-32 space-y-2.5 focus:outline-none"
    >
      <div className="flex items-end justify-between gap-3">
        {header}
        {steps.length > 1 && (
          <span className="text-fg-dim @2xl:hidden shrink-0 text-xs">
            {t('travel.arrival.swipe')}
          </span>
        )}
      </div>
      <ol className="@2xl:mr-0 @2xl:grid @2xl:grid-cols-2 @2xl:overflow-visible @2xl:pr-0 -mr-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1 pr-4 [scrollbar-width:none]">
        {steps.map((step, index) => (
          <li key={step.id} className="@2xl:w-auto w-[236px] shrink-0 snap-start">
            <ArrivalStepCard
              step={step}
              number={numbered ? index + 1 : undefined}
              icon={props.icon}
              token={token}
              onOpen={() => setOpen(index)}
            />
          </li>
        ))}
      </ol>
      {active && open !== null && (
        <GuestSheet
          open
          onOpenChange={(value) => {
            if (!value) setOpen(null);
          }}
          title={numbered ? `${open + 1}. ${active.title}` : active.title}
          closeLabel={t('travel.sheet.close')}
        >
          <div className="space-y-3">
            {active.body && (
              <p className="text-fg whitespace-pre-wrap text-[15px] leading-relaxed">
                {active.body}
              </p>
            )}
            <ArrivalStepGallery images={active.images ?? []} token={token} />
            {props.renderExtra?.(active)}
          </div>
        </GuestSheet>
      )}
    </section>
  );
}
